import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';

import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import HeaderDrawerMenu from '@perpay-web/fintech/components/base/HeaderDrawerMenu/HeaderDrawerMenu';
import { HOME_PAGE_URL } from '@perpay-web/fintech/constants/urls';
import { useCenterHeaderRender } from '@perpay-web/fintech/hooks/useCenterHeaderContext';
import { useHeaderLeftNavRender } from '@perpay-web/fintech/hooks/useHeaderLeftNavContext';
import { useHeaderRightNavRender } from '@perpay-web/fintech/hooks/useHeaderRightNavContext';
import { useShopPath } from '@perpay-web/fintech/hooks/useShopPath';
import {
    focusFlowPaths,
    focusFlowV2Paths,
    paths,
} from '@perpay-web/fintech/props/appPaths';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { getClassName } from '@perpay-web/utils/domUtils';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { isAppBrowserAgent } from '@perpay-web/utils/userAgentUtils';

import styles from './DefaultHeader.scss';

const DefaultHeader = ({
    className = '',
    path,
    userData = { fullName: '' },
    isAuthenticated = false,
    isPerpayPlusEligible,
    hasPerpayPlus,
    onClickPerpayPlus,
    launchSupportToolHandler,
    hasClosedCardAccount,
    analyticsHeaderOnClickDashboard,
    analyticsHeaderOnClickShop,
    analyticsHeaderOnClickHamburger,
}) => {
    const [navClicked, setNavClicked] = useState();
    // TODO: isMobile is used incorrectly, should be { isMobile } instead
    const isMobile = useIsMobile();
    const isAppBrowser = isAppBrowserAgent();
    const location = useLocation();
    const { lastShopPath } = useShopPath();

    const navClickHandler = () => {
        setNavClicked(!navClicked);
    };

    const renderDefaultRightAuthNav = () => {
        if (!isAuthenticated) {
            return null;
        }

        return (
            <div className={styles['secondary-navigation__block']}>
                <EndToEndTestIdProvider testId='header-drawer-icon'>
                    <CustomButton
                        action='link'
                        className={styles.profile__block}
                        onClick={() => {
                            navClickHandler();
                            analyticsHeaderOnClickHamburger();
                        }}
                    >
                        <div className={styles.icon__block} />
                    </CustomButton>
                </EndToEndTestIdProvider>
                {navClicked ? (
                    <HeaderDrawerMenu
                        onClose={() => navClickHandler()}
                        fullName={userData.fullName}
                        accountBalance={userData.accountBalance}
                        onClickPerpayPlus={onClickPerpayPlus}
                        launchSupportToolHandler={launchSupportToolHandler}
                        isPerpayPlusEligible={isPerpayPlusEligible}
                        hasClosedCardAccount={hasClosedCardAccount}
                    />
                ) : null}
            </div>
        );
    };

    const rightNavRendered = useHeaderRightNavRender();
    const rightNav =
        typeof rightNavRendered === 'undefined'
            ? renderDefaultRightAuthNav()
            : rightNavRendered;

    const leftNavRendered = useHeaderLeftNavRender();
    const leftNav =
        typeof leftNavRendered === 'undefined' ? null : leftNavRendered;

    const unauthLogoUrl = isAppBrowser ? paths.signUp.path : HOME_PAGE_URL;
    const headerLogoUrl = isAuthenticated
        ? paths.dashboard.path
        : unauthLogoUrl;
    const overrideHeaderContainerWidth =
        (!isAuthenticated || path === paths.signUp.path) && !isMobile;

    const renderedCenterHeader = useCenterHeaderRender();
    const renderDefaultCenterHeader = () => {
        if (!isAuthenticated) {
            return null;
        }

        if (
            focusFlowPaths.some((focusFlowPath) =>
                location.pathname.startsWith(focusFlowPath),
            )
        ) {
            return null;
        }

        return (
            <>
                <NavLink
                    onClick={analyticsHeaderOnClickDashboard}
                    to={paths.dashboard.path}
                    className={({ isActive }) =>
                        getClassName(
                            styles.block__text,
                            isActive ? styles['block__text--active'] : null,
                        )
                    }
                >
                    Dashboard
                </NavLink>
                <NavLink
                    onClick={analyticsHeaderOnClickShop}
                    to={lastShopPath}
                    className={({ isActive }) =>
                        getClassName(
                            styles.block__text,
                            isActive ? styles['block__text--active'] : null,
                        )
                    }
                >
                    Shop
                </NavLink>
            </>
        );
    };

    const centerHeader =
        typeof renderedCenterHeader !== 'undefined'
            ? renderedCenterHeader
            : renderDefaultCenterHeader();

    const transparentHeader = focusFlowV2Paths.some((focusFlowPath) =>
        location.pathname.startsWith(focusFlowPath),
    );

    return (
        <div
            className={getClassName(
                className,
                styles.header,
                transparentHeader ? styles['transparent-header'] : '',
            )}
        >
            <div
                className={`
                    ${styles.header__container} \
                    ${
                        overrideHeaderContainerWidth
                            ? styles['unauth--desktop-only']
                            : ''
                    } \
                `}
            >
                {/* Left Nav */}
                <Link
                    replace={
                        isAuthenticated
                            ? location.pathname === paths.dashboard.path
                            : false
                    }
                    className={getClassName(
                        styles.logo__block,
                        styles['link-image'],
                        hasPerpayPlus ? styles['logo--perpay-plus'] : '',
                    )}
                    to={headerLogoUrl}
                >
                    Go to the home page
                </Link>
                {leftNav ? (
                    <div className={styles['left-nav__container']}>
                        {leftNav}
                    </div>
                ) : null}
                {/* Center Nav */}
                <div className={styles['center-nav__container']}>
                    {centerHeader}
                </div>
                {/* Right Nav */}
                {rightNav}
            </div>
        </div>
    );
};

export default DefaultHeader;
